// SEO landing-page route configs.
import { formatFromPrice, formatFareShort } from './priceDisplay.js';

const FROM_28 = formatFromPrice(28);
const FROM_MOSHI = formatFromPrice(31.99);
const FARE_28 = formatFareShort(28);
const FARE_MOSHI = formatFareShort(31.99);
const FROM_28_CAP = FROM_28.replace(/^from/, 'From');
const FROM_MOSHI_CAP = FROM_MOSHI.replace(/^from/, 'From');

export const ROUTE_CONFIGS = [
  {
    slug: 'nairobi-to-arusha-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Nairobi to Arusha',
    h1: 'Nairobi to Arusha Shuttle Bus',
    title: `Nairobi to Arusha Shuttle Bus | From ${FARE_28}`,
    metaDescription:
      `Daily Nairobi–Arusha shuttle at 08:00 & 14:00, arriving in 5½–6 hours. Hotel pickup, Namanga border crossing, instant e-ticket. ${FROM_28_CAP}.`,
    intro: [
      "Our Nairobi to Arusha shuttle runs twice a day, every day of the week, with departures at 08:00 (arriving around 13:40) and 14:00 (arriving around 19:30). Both services are timed to get you across the Kenya–Tanzania border and into Arusha in daylight, in 5½–6 hours depending on how busy Namanga is when you cross.",
      "Pickup is included from your hotel or a central Nairobi meeting point, so you don't need to find your own way to a bus station. At Namanga you'll walk across the border yourself and clear Kenyan exit and Tanzanian entry immigration in person — allow around 45 minutes for this, and make sure any visa or eTA you need is sorted before you travel, as that part is on you rather than us.",
      "Each fare includes two bags up to 30 kg plus a backpack, and if your plans change you can move to a later date free of charge as long as you let us know at least 12 hours before departure. You'll get your e-ticket by email the moment you book, and the shuttle itself is run by local drivers who know this road well.",
    ],
    originCity: 'Nairobi',
    destCity: 'Arusha',
    departures: ['08:00', '14:00'],
    hotelPickupHint: 'Hotel pickup from 07:00 and 13:00',
    priceFromGbp: 28,
    duration: '5½–6 hours',
    highlights: [
      'Two daily departures: 08:00 and 14:00',
      'Hotel pickup included in Nairobi',
      'Free date changes up to 12 hours before departure',
      'Fares ' + FROM_28,
    ],
    faqs: [
      {
        q: 'What time does the Nairobi to Arusha shuttle leave?',
        a: "There are two departures every day, at 08:00 and 14:00, from a central Nairobi pickup point or your hotel if you're staying nearby.",
      },
      {
        q: 'How long does the crossing at Namanga take?',
        a: 'Budget around 45 minutes to clear Kenyan exit immigration and Tanzanian entry immigration on foot. Your shuttle waits for all passengers on the other side before continuing to Arusha.',
      },
      {
        q: 'Do I need a visa for Tanzania?',
        a: "Yes, most travellers need a Tanzanian visa. You can apply online for an eVisa before you travel, which we recommend, or get a visa on arrival at Namanga if your nationality is eligible. Arranging your entry documents is your responsibility, and we can't board you at Namanga without them.",
      },
      {
        q: 'How much luggage can I bring?',
        a: "Each ticket includes two bags up to 30 kg plus one backpack. Let us know in advance if you're travelling with more.",
      },
      {
        q: 'Can I change my travel date after booking?',
        a: 'Yes, date changes are free if requested at least 12 hours before your original departure time.',
      },
      {
        q: 'Is a private transfer available instead of the shared shuttle?',
        a: "Yes — if you'd rather travel on your own schedule in a private vehicle, see our Nairobi to Arusha private transfer option.",
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'arusha-to-nairobi-shuttle',
      'nairobi-to-arusha-private-transfer',
      'nairobi-airport-to-arusha-shuttle',
    ],
  },
  {
    slug: 'arusha-to-nairobi-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Arusha to Nairobi',
    h1: 'Arusha to Nairobi Shuttle Bus',
    title: `Arusha to Nairobi Shuttle Bus | From ${FARE_28}`,
    metaDescription:
      `Daily Arusha–Nairobi shuttle at 08:00 & 14:00. Namanga border crossing, hotel pickup, instant e-ticket confirmation. Fares ${FROM_28}.`,
    intro: [
      `The Arusha to Nairobi shuttle departs daily at 08:00 and 14:00, taking around 5½–6 hours to reach Nairobi once you include the stop at the Namanga border. Both departures are timed so you're not crossing in the dark, and the 08:00 service is a popular choice if you need to connect onward from Nairobi the same evening.`,
      "We'll collect you from your Arusha hotel or an agreed central meeting point. At Namanga you clear Tanzanian exit and Kenyan entry immigration yourself, on foot — it usually takes about 45 minutes — before rejoining the same shuttle on the Kenyan side.",
      'Two bags of up to 30 kg each plus a backpack are included per person, and you can change your travel date free of charge up to 12 hours before departure. Your e-ticket arrives by email as soon as you book.',
    ],
    originCity: 'Arusha',
    destCity: 'Nairobi',
    departures: ['08:00', '14:00'],
    hotelPickupHint: 'Hotel pickup from 07:30 and 13:30',
    priceFromGbp: 28,
    duration: '5½–6 hours',
    highlights: [
      'Two daily departures: 08:00 and 14:00',
      'Hotel pickup in Arusha included',
      'Namanga border crossing handled step by step',
      'Fares ' + FROM_28,
    ],
    faqs: [
      {
        q: 'What time does the Arusha to Nairobi shuttle depart?',
        a: 'Daily departures are at 08:00 and 14:00 from Arusha, arriving in Nairobi around 5½–6 hours later depending on the border.',
      },
      {
        q: 'Will the shuttle wait for me at Namanga?',
        a: 'Yes, the driver waits on the Kenyan side for all passengers to clear immigration before continuing to Nairobi.',
      },
      {
        q: 'Can I be dropped at JKIA instead of central Nairobi?',
        a: "Let us know your onward flight details when booking and we'll confirm whether an airport drop-off can be arranged on your service.",
      },
      {
        q: "What's the luggage allowance?",
        a: 'Two bags up to 30 kg each plus a backpack per passenger are included in the fare.',
      },
      {
        q: 'Is there a private transfer option for this route?',
        a: 'Yes, see our Nairobi to Arusha private transfer page for a whole-vehicle option that runs on your own schedule.',
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
      {
        q: 'Can I change my date after booking?',
        a: 'Yes, free of charge if you request the change at least 12 hours before your original departure.',
      },
    ],
    related: [
      'nairobi-to-arusha-shuttle',
      'kenya-to-tanzania-shuttle',
      'moshi-to-nairobi-shuttle',
    ],
  },
  {
    slug: 'nairobi-to-moshi-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Nairobi to Moshi',
    h1: 'Nairobi to Moshi Shuttle Bus',
    title: `Nairobi to Moshi Shuttle Bus | From ${FARE_MOSHI}`,
    metaDescription:
      `Daily Nairobi–Moshi shuttle at 08:00, 7–8 hours via Namanga. Ideal for Kilimanjaro climbers. Hotel pickup, ${FROM_MOSHI_CAP}.`,
    intro: [
      "Moshi is the main gateway town for climbing Kilimanjaro, and our Nairobi to Moshi shuttle runs daily at 08:00, taking 7–8 hours including the border stop at Namanga and continuing on through Arusha to Moshi.",
      "If you're heading to Moshi to start a Kilimanjaro trek, the 08:00 departure gets you in with daylight to spare to meet your trekking company and sort out final gear checks. Hotel pickup in Nairobi is included, and you'll clear Kenyan exit and Tanzanian entry immigration yourself at Namanga, which takes around 45 minutes.",
      "Fares include two bags up to 30 kg each plus a backpack — useful if you're carrying trekking kit — and you can change your date for free up to 12 hours before departure. You'll receive your e-ticket by email immediately after booking.",
    ],
    originCity: 'Nairobi',
    destCity: 'Moshi',
    departures: ['08:00'],
    hotelPickupHint: 'Hotel pickup from 07:00',
    priceFromGbp: 31.99,
    duration: '7–8 hours',
    highlights: [
      'Daily 08:00 departure',
      'Gateway route for Kilimanjaro climbers',
      'Namanga border crossing included in the journey',
      'Fares ' + FROM_MOSHI,
    ],
    faqs: [
      {
        q: 'How long does the Nairobi to Moshi shuttle take?',
        a: 'Journeys typically take 7–8 hours, including the stop to clear immigration at the Namanga border.',
      },
      {
        q: 'Is this a good option before a Kilimanjaro climb?',
        a: 'Yes, Moshi is where most Kilimanjaro trekking companies are based, and the daily 08:00 departure gets you in with time to spare before your climb briefing.',
      },
      {
        q: 'Do I pass through Arusha on the way to Moshi?',
        a: 'Yes, the route continues through Arusha to Moshi; your booking confirmation will show the schedule for your travel date.',
      },
      {
        q: 'What luggage can I bring for a trek?',
        a: 'Two bags up to 30 kg each plus a backpack are included, which is normally enough for trekking kit as well as regular luggage.',
      },
      {
        q: 'Can I change my departure date?',
        a: 'Yes, free of charge if you let us know at least 12 hours before your original departure time.',
      },
      {
        q: 'Do I need a Tanzanian visa?',
        a: "Most travellers do, and it's your responsibility to arrange it before travelling — we recommend sorting this well ahead of your Kilimanjaro trip.",
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'moshi-to-nairobi-shuttle',
      'namanga-to-moshi-shuttle',
      'kenya-to-tanzania-shuttle',
    ],
  },
  {
    slug: 'moshi-to-nairobi-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Moshi to Nairobi',
    h1: 'Moshi to Nairobi Shuttle Bus',
    title: `Moshi to Nairobi Shuttle Bus | From ${FARE_MOSHI}`,
    metaDescription:
      `Daily 06:00 Moshi–Nairobi shuttle (hotel pickup from 05:30), timed for afternoon arrival and evening flights from JKIA. ${FROM_MOSHI_CAP}, 7–8 hours.`,
    intro: [
      "If you're finishing a Kilimanjaro climb or safari around Moshi and need to catch an evening flight out of Nairobi, our daily 06:00 departure is built for exactly that. Hotel pickup from Moshi is from 05:30. The journey takes 7–8 hours including the Namanga border crossing, getting you into Nairobi or on to JKIA in the afternoon with time to spare.",
      "The early start means less time sitting in Nairobi waiting for a flight. At Namanga you'll clear Tanzanian exit and Kenyan entry immigration yourself, which takes about 45 minutes.",
      "Two bags up to 30 kg each plus a backpack are included, and you can move to a different date for free up to 12 hours before departure. Your e-ticket is emailed to you as soon as you book, so you'll have it ready to show at pickup.",
    ],
    originCity: 'Moshi',
    destCity: 'Nairobi',
    departures: ['06:00'],
    hotelPickupHint: 'Hotel pickup from 05:30',
    priceFromGbp: 31.99,
    duration: '7–8 hours',
    highlights: [
      '06:00 departure · hotel pickup from 05:30',
      'Timed for afternoon Nairobi/JKIA arrival',
      'Good fit for evening flights out of Nairobi',
      'Fares ' + FROM_MOSHI,
    ],
    faqs: [
      {
        q: 'Why does the Moshi to Nairobi shuttle leave so early?',
        a: 'The 06:00 departure (hotel pickup from 05:30 in Moshi) is timed to get you into Nairobi, or on to JKIA, in the afternoon so you have a comfortable buffer before an evening flight.',
      },
      {
        q: 'Will I make an evening flight from JKIA?',
        a: "Most passengers arriving on this service comfortably make evening departures from JKIA, but for very early evening flights we'd recommend checking the day's schedule with us before booking.",
      },
      {
        q: 'Where does pickup happen in Moshi?',
        a: 'Hotel pickup is from 05:30 for the 06:00 departure — we confirm your exact collection point when you book.',
      },
      {
        q: 'How long is the border crossing at Namanga?',
        a: 'Allow around 45 minutes to clear Tanzanian exit and Kenyan entry immigration on foot before rejoining the shuttle.',
      },
      {
        q: "What's the luggage allowance?",
        a: 'Two bags up to 30 kg each plus a backpack per passenger, which covers most trekking and safari kit.',
      },
      {
        q: 'Can I change my date if my climb finishes early or late?',
        a: "Yes, free of charge with at least 12 hours' notice before your original departure.",
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'nairobi-to-moshi-shuttle',
      'moshi-to-namanga-shuttle',
      'kenya-to-tanzania-shuttle',
    ],
  },
  {
    slug: 'nairobi-to-arusha-private-transfer',
    kind: 'private',
    breadcrumbLabel: 'Nairobi to Arusha Private Transfer',
    h1: 'Nairobi to Arusha Private Transfer',
    title: 'Nairobi to Arusha Private Transfer | Instant Quote',
    metaDescription:
      'Private vehicle from any Nairobi address, including JKIA, to Arusha. Instant online quote, your own schedule, door to door in 4½–5 hours.',
    intro: [
      "For travellers who'd rather not share a shuttle timetable, our Nairobi to Arusha private transfer puts a whole vehicle at your disposal, door to door in around 4½ to 5 hours. You can be picked up from a home address, hotel, or straight off a flight at JKIA, and set off whenever suits you rather than waiting for a fixed 08:00 or 14:00 departure.",
      "Because you're not sharing with other passengers, the journey through to Arusha and the Namanga border crossing tends to be quicker than the scheduled shuttle, and there's no need to coordinate with anyone else's plans. Get an instant online quote by entering your pickup and drop-off details — pricing depends on the exact addresses and vehicle size you need.",
      "Drivers are the same vetted, experienced team who run the Nairobi–Arusha corridor daily on our shuttle services, so you're getting local knowledge of the route and the border crossing without the fixed schedule. Payment is by secure card checkout and you'll get instant confirmation by email.",
    ],
    originCity: '',
    destCity: '',
    priceFromGbp: null,
    duration: '4½–5 hours door to door',
    highlights: [
      'Whole vehicle, just your group',
      'Pickup from any Nairobi address or JKIA',
      'Travel on your own schedule, not a fixed timetable',
      'Instant online quote and confirmation',
    ],
    faqs: [
      {
        q: 'How is a private transfer different from the shuttle bus?',
        a: 'A private transfer is a whole vehicle booked just for your group, picked up at a time you choose, rather than a shared bus running to a fixed 08:00/14:00 timetable.',
      },
      {
        q: 'Can you pick me up directly from JKIA?',
        a: "Yes, JKIA is a standard pickup point — just enter it as your origin when getting your quote and we'll match you with your flight landing time.",
      },
      {
        q: 'How much does a private transfer cost?',
        a: 'Pricing depends on your exact pickup and drop-off addresses and the vehicle size you need. Enter your details for an instant online quote before you book.',
      },
      {
        q: 'Do I still need to clear immigration at Namanga myself?',
        a: 'Yes, the border crossing itself works the same way as on the shuttle — you clear Kenyan exit and Tanzanian entry immigration in person, with your driver waiting on the other side.',
      },
      {
        q: 'How long does the private transfer take?',
        a: 'Typically 4½ to 5 hours door to door, a little faster than the shared shuttle since there are no other passenger stops.',
      },
      {
        q: 'Can I book a private transfer for a group?',
        a: 'Yes, larger vehicles are available for bigger groups — the quote tool will show options for your group size.',
      },
    ],
    related: [
      'nairobi-to-arusha-shuttle',
      'nairobi-airport-to-arusha-shuttle',
      'kenya-to-tanzania-shuttle',
    ],
  },
  {
    slug: 'kenya-to-tanzania-shuttle',
    kind: 'hub',
    breadcrumbLabel: 'Kenya to Tanzania Shuttle',
    h1: 'Kenya to Tanzania Shuttle Bus Routes',
    title: 'Kenya to Tanzania Shuttle Bus | Nairobi–Arusha–Moshi',
    metaDescription:
      `Compare daily shuttle routes between Kenya and Tanzania via Namanga: Nairobi, JKIA, Arusha and Moshi. Fares ${FROM_28}, hotel pickup included.`,
    intro: [
      `Travelling overland between Kenya and Tanzania almost always means crossing at Namanga, the main land border on the Nairobi–Arusha road, and it's the crossing every one of our shuttle routes uses. Whichever direction you're going, our shuttles run daily and start ${FROM_28}.`,
      'We run scheduled services between Nairobi (including a JKIA pickup option), Arusha and Moshi, with most routes departing at 08:00 and 14:00 and taking from 5½ hours upward depending on distance and how busy the border is. Hotel pickup is included at both ends, and each fare covers two bags up to 30 kg plus a backpack.',
      "At Namanga you'll clear exit and entry immigration on foot yourself — allow around 45 minutes — while your driver waits to continue the journey on the other side. If you'd rather not stick to a fixed timetable, a private vehicle transfer is also available on the Nairobi–Arusha route.",
    ],
    originCity: '',
    destCity: '',
    priceFromGbp: 28,
    duration: 'from 5½ hours',
    highlights: [
      'All routes cross at the Namanga border',
      `Daily departures, fares ${FROM_28}`,
      'Nairobi, JKIA, Arusha and Moshi covered',
      'Hotel pickup included at both ends',
    ],
    faqs: [
      {
        q: 'Which border do the Kenya–Tanzania shuttles use?',
        a: 'All of our shuttle routes cross at Namanga, the main road border between the two countries, roughly midway between Nairobi and Arusha.',
      },
      {
        q: 'Which routes do you run?',
        a: 'Nairobi to Arusha, Nairobi to Moshi, JKIA to Arusha, Moshi to Namanga, and the reverse Arusha to Nairobi, Moshi to Nairobi and Namanga to Moshi services, all departing daily.',
      },
      {
        q: 'How long does the border crossing add to the journey?',
        a: 'Budget around 45 minutes to clear exit and entry immigration on foot at Namanga; this is included in the overall journey times quoted for each route.',
      },
      {
        q: 'Do I need a visa to cross into Tanzania or back into Kenya?',
        a: 'Most travellers need entry documents for both countries: a Tanzania visa (apply online for an eVisa, or get a visa on arrival at Namanga if your nationality is eligible) and, for Kenya, an eTA (best done online before you travel, though it can also be completed at Namanga). Arranging them is your responsibility.',
      },
      {
        q: 'Is a private transfer available instead of a scheduled shuttle?',
        a: "Yes, on the Nairobi–Arusha route you can book a private vehicle with an instant online quote if you'd rather not follow a fixed timetable.",
      },
      {
        q: "What's the cheapest route?",
        a: `Fares start ${FROM_28} on the Nairobi–Arusha, JKIA–Arusha and Moshi–Namanga routes; Nairobi–Moshi and Moshi–Nairobi start ${FROM_MOSHI} given the extra distance.`,
      },
    ],
    related: [
      'nairobi-to-arusha-shuttle',
      'nairobi-to-moshi-shuttle',
      'nairobi-airport-to-arusha-shuttle',
    ],
  },
  {
    slug: 'nairobi-airport-to-arusha-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'JKIA to Arusha',
    h1: 'Nairobi Airport (JKIA) to Arusha Shuttle',
    title: `JKIA to Arusha Shuttle Bus | From ${FARE_28}`,
    metaDescription:
      `Shuttle from Nairobi Airport (JKIA) to Arusha at 08:30 & 14:30, arriving in about 5 hours. No need to enter the city. Fares ${FROM_28}.`,
    intro: [
      `Landing at JKIA and heading straight on to Arusha? Our airport shuttle picks up from Jomo Kenyatta International Airport at 08:30 (arriving Arusha around 13:40) and 14:30 (arriving around 19:30), so there's no need to detour into central Nairobi first.`,
      "The journey takes about 5 hours, including the stop at the Namanga border where you'll clear Kenyan exit and Tanzanian entry immigration yourself — allow roughly 45 minutes for this. Drivers meet you at an agreed point in the arrivals area, so build in some buffer after your flight lands before the shuttle's scheduled pickup time.",
      "Each fare includes two bags up to 30 kg plus a backpack, ideal if you've just landed with full luggage. You can change your date for free up to 12 hours before departure, and your e-ticket is emailed to you as soon as you book, ready to show at the pickup point.",
    ],
    originCity: 'JKIA',
    destCity: 'Arusha',
    departures: ['08:30', '14:30'],
    priceFromGbp: 28,
    duration: '~5 hours',
    highlights: [
      'Pickup directly from JKIA arrivals',
      'Departures at 08:30 and 14:30',
      'No need to detour into Nairobi city',
      'Fares ' + FROM_28,
    ],
    faqs: [
      {
        q: 'Where exactly at JKIA does the shuttle pick up?',
        a: "At an agreed point in the arrivals area — we'll send you the exact meeting instructions and a contact number after booking.",
      },
      {
        q: 'How much time should I leave after my flight lands?',
        a: 'We recommend at least 90 minutes to clear immigration and baggage claim before your scheduled shuttle pickup time.',
      },
      {
        q: 'Does this shuttle go into Nairobi city first?',
        a: 'No, it goes directly from JKIA to Arusha via Namanga, so there is no detour into the city centre.',
      },
      {
        q: 'How long does the journey take?',
        a: 'Around 5 hours in total, including the Namanga border stop where you clear immigration yourself.',
      },
      {
        q: 'What if my flight is delayed?',
        a: 'Contact us as soon as you know — we will do our best to move you to the later 14:30 departure if you miss the 08:30 service.',
      },
      {
        q: 'Is a private transfer available from JKIA instead?',
        a: "Yes, our Nairobi to Arusha private transfer can also pick up from JKIA if you'd prefer your own vehicle and schedule.",
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'nairobi-to-arusha-shuttle',
      'nairobi-to-arusha-private-transfer',
      'kenya-to-tanzania-shuttle',
    ],
  },
  {
    slug: 'moshi-to-namanga-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Moshi to Namanga',
    h1: 'Moshi to Namanga Shuttle Bus',
    title: `Moshi to Namanga Shuttle Bus | From ${FARE_28}`,
    metaDescription:
      `Daily 06:00 Moshi–Namanga shuttle via Arusha, arriving at the Kenya–Tanzania border in about 4 hours. Hotel pickup from 05:30. Fares ${FROM_28}.`,
    intro: [
      `The Moshi to Namanga shuttle leaves Moshi daily at 06:00, calling at Arusha around 07:30, and continues to the Namanga border — the same early coach that runs through toward Nairobi. The journey takes about 3½–4½ hours depending on the Arusha stop and how busy the border road is.`,
      "Hotel pickup in Moshi is from 05:30 (hotels between about 05:15 and 05:45). You alight at Namanga Border Post; if you are continuing into Kenya you clear Tanzanian exit and Kenyan entry immigration on foot yourself — allow around 45 minutes — and make sure any visa or eTA you need is arranged before you travel.",
      "Each fare includes two bags up to 30 kg plus a backpack, and you can change your travel date free of charge up to 12 hours before departure. Your e-ticket arrives by email as soon as you book.",
    ],
    originCity: 'Moshi',
    destCity: 'Namanga',
    departures: ['06:00'],
    hotelPickupHint: 'Hotel pickup from 05:30',
    priceFromGbp: 28,
    duration: '3½–4½ hours',
    highlights: [
      '06:00 departure · hotel pickup from 05:30',
      'Calls at Arusha around 07:30',
      'Alight at Namanga Border Post',
      'Fares ' + FROM_28,
    ],
    faqs: [
      {
        q: 'What time does the Moshi to Namanga shuttle leave?',
        a: 'Daily at 06:00 from Moshi, with hotel pickup from 05:30. Collection from hotels can be from about 05:15 to 05:45. The coach calls at Arusha around 07:30 before continuing to the Namanga border.',
      },
      {
        q: 'How long does Moshi to Namanga take?',
        a: 'Typically 3½–4½ hours, including the Arusha call. Border processing is extra if you are walking through into Kenya.',
      },
      {
        q: 'Is this the same bus as Moshi to Nairobi?',
        a: 'Yes — you ride the morning Moshi–Nairobi through service and alight at Namanga rather than continuing to Nairobi.',
      },
      {
        q: 'Do I need a visa if I am only going to the border?',
        a: "If you stay on the Tanzanian side you do not enter Kenya. If you walk across into Kenya, most travellers need a Kenya eTA. It's best done online before you travel, though it can also be completed at Namanga. Arranging it is your responsibility.",
      },
      {
        q: "What's the luggage allowance?",
        a: 'Two bags up to 30 kg each plus a backpack per passenger are included in the fare.',
      },
      {
        q: 'Can I change my date after booking?',
        a: 'Yes, free of charge if you request the change at least 12 hours before your original departure.',
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'namanga-to-moshi-shuttle',
      'moshi-to-nairobi-shuttle',
      'kenya-to-tanzania-shuttle',
    ],
  },
  {
    slug: 'namanga-to-moshi-shuttle',
    kind: 'bus',
    breadcrumbLabel: 'Namanga to Moshi',
    h1: 'Namanga to Moshi Shuttle Bus',
    title: `Namanga to Moshi Shuttle Bus | From ${FARE_28}`,
    metaDescription:
      `Daily 11:00 Namanga–Moshi shuttle via Arusha, about 4–5 hours. Board at Namanga Border Post on the Nairobi–Moshi through coach. Fares ${FROM_28}.`,
    intro: [
      `The Namanga to Moshi shuttle boards at Namanga Border Post daily at 11:00, riding the morning Nairobi–Moshi through coach. You continue via Arusha (around 13:40) and on to Moshi, arriving in about 4–5 hours depending on the border and the Arusha stop.`,
      "There is no hotel pickup at Namanga — meet the coach at the border. Estimated pick-up time can vary with immigration queues, so contact dispatch if you are unsure of the day's running. If you have just crossed from Kenya, clear Kenyan exit and Tanzanian entry immigration on foot before boarding; allow around 45 minutes and have any visa or eTA ready.",
      "Each fare includes two bags up to 30 kg plus a backpack, and you can change your travel date free of charge up to 12 hours before departure. Your e-ticket is emailed as soon as you book.",
    ],
    originCity: 'Namanga',
    destCity: 'Moshi',
    departures: ['11:00'],
    priceFromGbp: 28,
    duration: '4–5 hours',
    highlights: [
      '11:00 departure from Namanga Border Post',
      'Continues via Arusha around 13:40',
      'Same through coach as Nairobi to Moshi',
      'Fares ' + FROM_28,
    ],
    faqs: [
      {
        q: 'What time does the Namanga to Moshi shuttle leave?',
        a: 'Daily at 11:00 from Namanga Border Post. Times can slip when the border is busy — contact dispatch on the day if you need an updated arrival.',
      },
      {
        q: 'How long does Namanga to Moshi take?',
        a: 'Typically 4–5 hours, including the Arusha call around 13:40 and onward running time to Moshi.',
      },
      {
        q: 'Is this the same bus as Nairobi to Moshi?',
        a: 'Yes — you board the morning Nairobi–Moshi through service at Namanga rather than in Nairobi or at JKIA.',
      },
      {
        q: 'Where do I wait at Namanga?',
        a: 'At Namanga Border Post on the side matching your onward journey into Tanzania. The driver waits for passengers clearing immigration before continuing.',
      },
      {
        q: 'Do I need a Tanzanian visa?',
        a: "If you are entering Tanzania at Namanga, most travellers need a Tanzanian visa. You can apply online for an eVisa before you travel, which we recommend, or get a visa on arrival at Namanga if your nationality is eligible. Arranging it is your responsibility.",
      },
      {
        q: 'Can I change my date after booking?',
        a: 'Yes, free of charge if you request the change at least 12 hours before your original departure.',
      },
      {
        q: 'Can I pay with M-Pesa / Mobile Payments?',
        a: 'You can pay with Airtel Money: switch the currency selector to TZS on the booking form. M-Pesa (KES) is not available yet. Cards (GBP, USD or EUR) and PayPal are also accepted at checkout.',
      },
    ],
    related: [
      'moshi-to-namanga-shuttle',
      'nairobi-to-moshi-shuttle',
      'kenya-to-tanzania-shuttle',
    ],
  },
];

export function getRouteBySlug(slug) {
  return ROUTE_CONFIGS.find((route) => route.slug === slug) || null;
}
